/**
 * Componente de filtros para Regional e UF
 */
import { useUnidadesFilters } from '../hooks/useUnidadesFilters';
import { useRegionals } from '../hooks/useUnitsData';
import { REGIONALS, REGIONAL_UFS, type Regional, type BrazilUF } from '@claro/shared';
import { cn } from '@/lib/utils';
import { Filter, X, ChevronDown } from 'lucide-react';

interface UnidadesFiltersProps {
  className?: string;
}

export function UnidadesFilters({ className }: UnidadesFiltersProps) {
  const { regional, uf, setRegional, setUF, clearFilters, hasActiveFilters } = useUnidadesFilters();
  const { data: regionalsData, isLoading } = useRegionals();

  // UFs disponíveis baseado na regional selecionada
  const availableUFs = regional ? (REGIONAL_UFS[regional] || []) : [];

  // Usa regionais da API se disponível, senão usa constantes
  const regionalsToShow = regionalsData?.length 
    ? regionalsData 
    : REGIONALS;

  // Handler para mudança de regional
  const handleRegionalChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setRegional(value ? (value as Regional) : null);
  };

  // Handler para mudança de UF
  const handleUFChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setUF(value ? (value as BrazilUF) : null);
  };

  return (
    <div className={cn('flex items-center gap-3 flex-wrap', className)}>
      {/* Ícone de filtro */}
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <Filter className="w-4 h-4" />
        <span className="text-sm font-medium">Filtros:</span>
      </div>

      {/* Select de Regional */}
      <div className="relative">
        <select
          id="regional-filter"
          value={regional || ''}
          onChange={handleRegionalChange}
          disabled={isLoading}
          style={{ minWidth: '160px' }}
          className={cn(
            'h-9 pl-3 pr-8 text-sm rounded-md border bg-background text-foreground appearance-none cursor-pointer',
            'focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'transition-colors',
            regional && 'border-primary/50 bg-primary/5'
          )}
        >
          <option value="">Todas Regionais</option>
          {regionalsToShow.map((reg) => (
            <option key={reg} value={reg}>
              {reg}
            </option>
          ))}
        </select>
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
      </div>

      {/* Select de UF (só aparece se regional selecionada) */}
      {regional && availableUFs.length > 0 && (
        <div className="relative">
          <select
            id="uf-filter"
            value={uf || ''}
            onChange={handleUFChange}
            style={{ minWidth: '100px' }}
            className={cn(
              'h-9 pl-3 pr-8 text-sm rounded-md border bg-background text-foreground appearance-none cursor-pointer',
              'focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary',
              'transition-colors',
              uf && 'border-primary/50 bg-primary/5'
            )}
          >
            <option value="">Todos UFs</option>
            {availableUFs.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        </div>
      )}

      {/* Botão limpar filtros */}
      {hasActiveFilters() && (
        <button
          onClick={clearFilters}
          className={cn(
            'flex items-center gap-1.5 px-3 h-9 text-sm rounded-md',
            'bg-destructive/10 text-destructive hover:bg-destructive/20',
            'transition-colors font-medium'
          )}
        >
          <X className="w-3.5 h-3.5" />
          <span>Limpar</span>
        </button>
      )}

      {/* Indicador de filtros ativos */}
      {hasActiveFilters() && (
        <div className="flex items-center gap-2">
          <div className="h-5 w-px bg-border" />
          <div className="flex items-center gap-1.5 text-xs bg-muted px-2 py-1 rounded-full">
            <span className="text-muted-foreground">Filtrado:</span>
            <span className="font-medium text-foreground">
              {[regional, uf].filter(Boolean).join(' → ')}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
